import { useState, useEffect, useCallback } from 'react';

// Normalize phone number - remove +91 prefix and spaces
export function normalizePhone(phone: string): string {
  return phone.replace(/\+91\s*/, '').replace(/\s/g, '').trim();
}

export interface CustomerUser {
  name: string;
  phone: string;
  email?: string;
  address?: {
    fullName: string;
    phone: string;
    address: string;
    city: string;
    pincode: string;
    lat?: number;
    lng?: number;
    mapAddress?: string;
  };
  registeredAt: string;
}

const AUTH_KEY = 'clothybazar_customer_auth';

// Get phone-specific address key
function getAddressKey(phone: string): string {
  const normalized = normalizePhone(phone);
  return `clothybazar_address_${normalized}`;
}

export function getCustomerUser(): CustomerUser | null {
  try {
    const data = localStorage.getItem(AUTH_KEY);
    if (!data) return null;
    const user = JSON.parse(data) as CustomerUser;
    // Normalize phone number when reading from storage
    if (user.phone) {
      user.phone = normalizePhone(user.phone);
    }
    return user;
  } catch {
    return null;
  }
}

export function saveCustomerUser(user: CustomerUser): void {
  // Normalize phone number before saving
  const normalizedPhone = normalizePhone(user.phone);
  const normalizedUser = {
    ...user,
    phone: normalizedPhone,
    address: user.address ? {
      ...user.address,
      phone: normalizePhone(user.address.phone),
    } : undefined,
  };
  localStorage.setItem(AUTH_KEY, JSON.stringify(normalizedUser));
  
  // Also save address separately with phone-specific key for persistence across logins
  if (user.address) {
    const addressKey = getAddressKey(normalizedPhone);
    localStorage.setItem(addressKey, JSON.stringify(user.address));
  }
}

/** Get saved address for a specific phone number (persists across logins) */
export function getAddressForPhone(phone: string): CustomerUser['address'] | null {
  try {
    const key = getAddressKey(normalizePhone(phone));
    const saved = localStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return null;
}

/** Save address for a specific phone number */
export function saveAddressForPhone(phone: string, address: CustomerUser['address']): void {
  try {
    const key = getAddressKey(normalizePhone(phone));
    localStorage.setItem(key, JSON.stringify(address));
  } catch { /* ignore */ }
}

export function clearCustomerUser(): void {
  localStorage.removeItem(AUTH_KEY);
}

// Custom event name for same-tab auth changes
const AUTH_CHANGE_EVENT = 'clothybazar_auth_change';

export function useCustomerAuth() {
  const [user, setUser] = useState<CustomerUser | null>(() => getCustomerUser());
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => !!getCustomerUser());

  const applyUser = useCallback((newUser: CustomerUser | null) => {
    setUser(newUser);
    setIsLoggedIn(!!newUser);
  }, []);

  // Sync across tabs (storage event)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === AUTH_KEY) {
        const newUser = e.newValue ? JSON.parse(e.newValue) as CustomerUser : null;
        applyUser(newUser);
      }
    };
    // Same-tab custom event
    const handleAuthChange = () => {
      applyUser(getCustomerUser());
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener(AUTH_CHANGE_EVENT, handleAuthChange);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(AUTH_CHANGE_EVENT, handleAuthChange);
    };
  }, [applyUser]);

  const syncFromStorage = useCallback(() => {
    const current = getCustomerUser();
    applyUser(current);
    return current;
  }, [applyUser]);

  const login = useCallback((newUser: CustomerUser) => {
    const normalizedPhone = normalizePhone(newUser.phone);
    
    // Check if there's a saved address for this phone number
    const savedAddress = getAddressForPhone(normalizedPhone);
    
    // Merge saved address with new user data (priority: saved address > new user address)
    const userWithAddress: CustomerUser = {
      ...newUser,
      phone: normalizedPhone,
      address: savedAddress || newUser.address || undefined,
    };
    
    saveCustomerUser(userWithAddress);
    applyUser(userWithAddress);
    // Broadcast to all components in same tab
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  }, [applyUser]);

  const logout = useCallback(() => {
    clearCustomerUser();
    applyUser(null);
    // NOTE: userAddress is NOT cleared on logout — it contains shipping address
    // linked to phone number and should persist for next login with same number
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  }, [applyUser]);

  const updateUser = useCallback((updates: Partial<CustomerUser>) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      saveCustomerUser(updated);
      return updated;
    });
  }, []);

  return { user, login, logout, updateUser, isLoggedIn, syncFromStorage };
}
